import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertCircle, Info, AlertTriangle, X } from "lucide-react";

/**
 * Modern Floating Toast Popup Component
 * Uses React createPortal to mount directly to document.body so that
 * it NEVER scrolls with parent containers, gets clipped, or overlaps headers.
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the popup is visible
 * @param {'success'|'error'|'warning'|'info'} props.type - Type of toast
 * @param {string} props.title - Toast title
 * @param {string} props.message - Toast message
 * @param {function} props.onClose - Function to close toast
 * @param {number} [props.autoClose=4500] - Duration in ms before auto-closing
 * @param {'top-right'|'bottom-right'} [props.position='top-right'] - Screen position
 */
const ToastPopup = ({
  isOpen,
  type = "error",
  title,
  message,
  onClose,
  autoClose = 4500,
  position = "top-right",
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen && autoClose > 0) {
      const timer = setTimeout(() => {
        if (onClose) onClose();
      }, autoClose);
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, onClose]);

  const config = {
    error: {
      border: "border-red-300",
      accent: "bg-red-500",
      icon: <AlertCircle className="w-5 h-5 text-red-600 shrink-0" />,
      iconBg: "bg-red-100",
      titleColor: "text-red-950",
      textColor: "text-red-800",
      badge: "Validation Error",
    },
    success: {
      border: "border-emerald-300",
      accent: "bg-emerald-500",
      icon: <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />,
      iconBg: "bg-emerald-100",
      titleColor: "text-emerald-950",
      textColor: "text-emerald-800",
      badge: "Success",
    },
    warning: {
      border: "border-amber-300",
      accent: "bg-amber-500",
      icon: <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />,
      iconBg: "bg-amber-100",
      titleColor: "text-amber-950",
      textColor: "text-amber-800",
      badge: "Attention",
    },
    info: {
      border: "border-blue-300",
      accent: "bg-blue-500",
      icon: <Info className="w-5 h-5 text-blue-600 shrink-0" />,
      iconBg: "bg-blue-100",
      titleColor: "text-blue-950",
      textColor: "text-blue-800",
      badge: "Information",
    },
  };

  const style = config[type] || config.info;

  if (!mounted || typeof document === "undefined") {
    return null;
  }

  // Determine fixed screen position
  const isBottom = position === "bottom-right";
  const positionClass = isBottom
    ? "fixed bottom-24 right-4 sm:right-6 z-[9999999] w-[90%] max-w-sm pointer-events-none"
    : "fixed top-28 right-4 sm:right-6 z-[9999999] w-[90%] max-w-sm pointer-events-none";

  const motionInitial = isBottom
    ? { opacity: 0, y: 20, scale: 0.95 }
    : { opacity: 0, y: -20, scale: 0.95 };

  const motionExit = isBottom
    ? { opacity: 0, y: 15, scale: 0.95 }
    : { opacity: 0, y: -15, scale: 0.95 };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className={positionClass}>
          <motion.div
            initial={motionInitial}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={motionExit}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className={`pointer-events-auto relative overflow-hidden rounded-2xl border-2 ${style.border} bg-white p-4 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.28)]`}
          >
            {/* Left Color Accent Bar */}
            <div className={`absolute top-0 left-0 bottom-0 w-2 ${style.accent}`} />

            <div className="flex items-start gap-3.5 pl-1">
              {/* Icon */}
              <div className={`p-2 rounded-xl ${style.iconBg} mt-0.5 shadow-sm`}>
                {style.icon}
              </div>

              {/* Text Body */}
              <div className="flex-1 pr-2 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className={`text-xs font-bold uppercase tracking-wider ${style.titleColor}`}>
                    {title || style.badge}
                  </span>
                </div>
                {message && (
                  <p className={`text-xs font-medium leading-relaxed ${style.textColor}`}>
                    {message}
                  </p>
                )}
              </div>

              {/* Close Button */}
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors shrink-0"
                  aria-label="Close"
                >
                  <X size={15} />
                </button>
              )}
            </div>

            {/* Progress bar countdown */}
            {autoClose > 0 && (
              <motion.div
                initial={{ width: "100%" }}
                animate={{ width: "0%" }}
                transition={{ duration: autoClose / 1000, ease: "linear" }}
                className={`absolute bottom-0 left-0 h-0.5 ${style.accent} opacity-40`}
              />
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ToastPopup;
